import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { PropsWithChildren, useCallback, useEffect } from "react";

import { REFRESH_TOKEN_URL } from "@hd/api/auth/requests/refresh-token-request";
import { useAuth } from "@hd/auth/useAuth";
import i18n from "@hd/i18n/init";
import { useI18n } from "@hd/i18n/useI18n";
import { useLoader } from "@hd/loader/useLoader";
import { errorLog } from "@hd/logger/logger";

import { SILENT_REQUEST_HEADER_KEY } from "./constants";
import httpClient from "./http-client";
import { ApiError, AxiosAuthRefreshCache } from "./types";

// 🔹 coda tipizzata
type MfaQueueEntry = {
  resolve: (value: AxiosResponse<unknown>) => void;
  reject: (error: unknown) => void;
  config: InternalAxiosRequestConfig;
};

let mfaQueue: MfaQueueEntry[] = [];

export const enqueueMfaRequest = (
  config: InternalAxiosRequestConfig
): Promise<AxiosResponse<unknown>> => {
  return new Promise((resolve, reject) => {
    mfaQueue.push({ config, resolve, reject });
  });
};

export const processMfaQueue = (newToken: string) => {
  mfaQueue.forEach(({ config, resolve, reject }) => {
    config.headers.Authorization = `Bearer ${newToken}`;
    axios(config)
      .then((response: AxiosResponse<unknown>) => resolve(response))
      .catch(reject);
  });
  mfaQueue = [];
};

export const clearMfaQueue = () => {
  const error = new ApiError(
    "MFA_CANCELLED",
    i18n.t("stepUpModal.mfaCancelled"),
    403
  );
  mfaQueue.forEach(({ reject }) => reject(error));
  mfaQueue = [];
};

// 🔥 cache per il refresh come prima
const cache: AxiosAuthRefreshCache = {
  refreshCall: undefined,
  requestQueueInterceptorId: undefined,
};

function createRequestQueueInterceptor(
  instance: AxiosInstance,
  cache: AxiosAuthRefreshCache
): number {
  if (typeof cache.requestQueueInterceptorId === "undefined") {
    cache.requestQueueInterceptorId = instance.interceptors.request.use(
      (request) => {
        return (cache.refreshCall as Promise<unknown>)
          .then(() => request)
          .catch(() => {
            throw new axios.Cancel("Request call failed");
          });
      }
    );
  }
  return cache.requestQueueInterceptorId;
}

function createRefreshCall(
  error: AxiosError,
  fn: (error: AxiosError) => Promise<unknown>,
  cache: AxiosAuthRefreshCache
): Promise<unknown> {
  if (!cache.refreshCall) {
    cache.refreshCall = fn(error);
  }
  return cache.refreshCall;
}

function unsetCache(
  instance: AxiosInstance,
  cache: AxiosAuthRefreshCache
): void {
  if (cache.requestQueueInterceptorId)
    instance.interceptors.request.eject(cache.requestQueueInterceptorId);
  cache.requestQueueInterceptorId = undefined;
  cache.refreshCall = undefined;
}

const getApiErrorMessage = (error: unknown) => {
  const defaultMsg = "Si è verificato un errore imprevisto";
  if (error instanceof AxiosError) {
    if (error.response?.data) {
      if (typeof error.response.data === "string") {
        return error.response.data;
      } else if (Array.isArray(error.response.data)) {
        return error.response.data.join("<br>");
      } else if (typeof error.response.data === "object") {
        if (error.response.data.error) {
          return error.response.data.error;
        } else if (error.response.data.message) {
          return error.response.data.message;
        }
      }
    }
  } else if (error instanceof Error) {
    return error.message;
  }

  return defaultMsg;
};

const throwErrorAndLog = (error: AxiosError) => {
  errorLog("API Error: ", error);
  const message = getApiErrorMessage(error);
  const apiError = new ApiError(error.name, message, error.status);

  return Promise.reject(apiError);
};

export const HttpClientInterceptorProvider = (props: PropsWithChildren) => {
  const { addLoader, removeLoader } = useLoader();
  const { getToken, refreshToken, sessionExpired, setRequiresMfaStepUp } =
    useAuth();
  const i18n = useI18n();

  const refreshCall = useCallback(async () => {
    await refreshToken();
  }, [refreshToken]);

  useEffect(() => {
    const requestInterceptor = (config: InternalAxiosRequestConfig) => {
      if (!config.headers.get(SILENT_REQUEST_HEADER_KEY)) {
        addLoader();
      }
      const token = getToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      config.headers["Accept-Language"] = i18n.language;
      return config;
    };

    const responseErrorInterceptor = (error: AxiosError) => {
      const status = error.response?.status;

      // 🔹 gestione refresh token
      if (error.config?.url !== REFRESH_TOKEN_URL && status === 401) {
        const refreshPromise = createRefreshCall(error, refreshCall, cache);
        createRequestQueueInterceptor(httpClient, cache);

        return refreshPromise
          .catch((refreshError: AxiosError) => {
            sessionExpired();
            return throwErrorAndLog(refreshError);
          })
          .then(() => {
            const token = getToken();
            const originalRequest = error.config as InternalAxiosRequestConfig;
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest as InternalAxiosRequestConfig);
          })
          .finally(() => {
            if (
              error.config &&
              !error.config.headers.get(SILENT_REQUEST_HEADER_KEY)
            ) {
              removeLoader();
            }
            unsetCache(httpClient, cache);
          });
      }

      // 🔹 gestione MFA step-up
      if (
        status === 403 &&
        (
          error.response?.data as {
            error: string;
            mfaType?: number;
            mfaMaskedEmail?: string;
          }
        ).error === "MFA_REQUIRED"
      ) {
        const originalRequest = error.config as InternalAxiosRequestConfig;
        const errorData = error.response?.data as {
          error: string;
          mfaType?: number;
          mfaMaskedEmail?: string;
        };

        // enqueue della request
        const promise = enqueueMfaRequest(originalRequest);

        // segnalo a React che serve MFA
        setRequiresMfaStepUp(
          true,
          errorData.mfaType != null ? errorData.mfaType : null,
          errorData.mfaMaskedEmail || null
        );

        // non loggo come errore qui → ritorno la promise
        return promise.finally(() => {
          if (
            error.config &&
            !error.config.headers.get(SILENT_REQUEST_HEADER_KEY)
          ) {
            removeLoader();
          }
        });
      }

      if (
        error.config &&
        !error.config.headers.get(SILENT_REQUEST_HEADER_KEY)
      ) {
        removeLoader();
      }

      return throwErrorAndLog(error);
    };

    const requestInterceptorId =
      httpClient.interceptors.request.use(requestInterceptor);

    const responseInterceptorId = httpClient.interceptors.response.use(
      (response) => {
        if (
          response.config &&
          !response.config.headers.get(SILENT_REQUEST_HEADER_KEY)
        ) {
          removeLoader();
        }
        return response;
      },
      responseErrorInterceptor
    );

    return () => {
      httpClient.interceptors.request.eject(requestInterceptorId);
      httpClient.interceptors.response.eject(responseInterceptorId);
    };
  }, [
    addLoader,
    removeLoader,
    sessionExpired,
    getToken,
    refreshCall,
    i18n.language,
    setRequiresMfaStepUp,
  ]);

  return props.children;
};
